import { buildSearchParamsString } from '@/utils/buildSearchParamsString'
import Cookie from 'js-cookie'
import QueryStringManager from '../QueryStringManager'

// Chiavi di campagna riconosciute (ETS - Event Tracking System)
const CAMPAIGN_KEYS = ['ets_cmmk', 'ets_sgmt', 'ets_sggt', 'ets_sggt_t']

// Durata di default per i cookie di campagna (30 giorni)
const DEFAULT_COOKIE_EXPIRATION = 30

// Pattern di validazione per i valori delle chiavi ETS
const ETS_PATTERN = /^[A-Z0-9_-]{1,32}$/i

/**
 * Estrae parametri di campagna da query, cookie o default, per chiave.
 *
 * Per ogni chiave ETS la priorità è:
 * 1. Query (link di atterraggio) — anche se il link porta solo una parte delle chiavi
 * 2. Cookie
 * 3. Parametri di default
 *
 * `queryString` accetta sia la stringa (`?ets_cmmk=abc&ets_sgmt=xyz`) sia
 * l'oggetto `searchParams` di Next.js 14.
 *
 * @param {string} cookieName - Nome del cookie da cui leggere
 * @param {QueryObject} [defaultParams={}] - Parametri di fallback
 * @param {string | QueryObject} [queryString=''] - Query string o oggetto searchParams
 * @returns {QueryObject} Oggetto con i parametri di campagna validi
 *
 * @example
 * const params = getCampaignParameters('campaign_utm', {}, '?ets_cmmk=abc&ets_sgmt=xyz')
 *  { ets_cmmk: 'abc', ets_sgmt: 'xyz' }
 */

export function getCampaignParameters(
  cookieName,
  defaultParams = {},
  queryString = ''
) {
  if (!cookieName) {
    console.warn('[CampaignManager] cookieName is required')
    return {}
  }

  const query = new QueryStringManager(_normalizeQueryString(queryString))
  const queryParams = _pickCampaignParams(query.getAll())

  let cookieParams = {}
  const cookieValue = Cookie.get(cookieName)
  if (cookieValue) {
    try {
      cookieParams = _pickCampaignParams(
        JSON.parse(decodeURIComponent(cookieValue))
      )
    } catch (error) {
      // Don't log the error object: a JSON parse error message echoes a
      // snippet of the cookie value (ETS tracking data). Log only its type.
      console.error('[CampaignManager] Cookie parsing error:', error?.name)
    }
  }

  const fallbackParams = _pickCampaignParams(defaultParams)
  const campaignParams = {}

  CAMPAIGN_KEYS.forEach((key) => {
    if (key in queryParams) campaignParams[key] = queryParams[key]
    else if (key in cookieParams) campaignParams[key] = cookieParams[key]
    else if (key in fallbackParams) campaignParams[key] = fallbackParams[key]
  })

  if (Object.keys(campaignParams).length) {
    // Log only which ETS keys were resolved, never their values (cookie/query
    // tracking data).
    console.info(
      '[CampaignManager] Campaign parameters retrieved:',
      Object.keys(campaignParams)
    )
  }

  return campaignParams
}

/**
 * Salva parametri di campagna in un cookie con opzioni avanzate
 *
 * @param {Object} options - Configurazione
 * @param {string} options.cookieName - Nome del cookie
 * @param {string} [options.params] - Parametri da estrarre (comma-separated, es: 'ets_cmmk,ets_sgmt')
 * @param {string} [options.domain] - Dominio del cookie (default: current domain)
 * @param {string} [options.path] - Path del cookie (default: '/')
 * @param {number} [options.expires] - Giorni di scadenza (default: 30)
 * @param {string} [options.queryString] - Query string da cui estrarre i parametri
 * @param {QueryObject} [options.customParams] - Parametri custom da aggiungere
 * @param {boolean} [options.override=false] - Se true, sostituisce i vecchi parametri
 * @returns {boolean} true se il salvataggio è riuscito, false altrimenti
 *
 * @example
 * setCampaignParameters({
 *   cookieName: 'campaign_utm',
 *   params: 'ets_cmmk,ets_sgmt',
 *   queryString: '?ets_cmmk=abc&ets_sgmt=xyz',
 *   expires: 30
 * })
 */
export function setCampaignParameters({
  cookieName,
  params = '',
  domain = undefined,
  path = '/',
  expires = DEFAULT_COOKIE_EXPIRATION,
  queryString = '',
  customParams = {},
  override = false,
}) {
  if (!cookieName) {
    console.warn('[CampaignManager] cookieName is required')
    return false
  }

  const query = new QueryStringManager(queryString)

  // Estrae i parametri richiesti dalla query string
  let newParams = {}

  if (params) {
    const paramList = params
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
    paramList.forEach((key) => {
      const value = query.get(key)
      if (value !== null) {
        newParams[key] = value
      }
    })
  } else {
    // Se params non è specificato, estrae tutti i parametri di campagna dalla query
    newParams = query.getAll()
  }

  // Aggiunge parametri custom
  if (Object.keys(customParams).length) {
    newParams = { ...newParams, ...customParams }
  }

  // Se non override, mergia con i parametri esistenti
  if (override === false) {
    const existingParams = getCampaignParameters(cookieName)
    newParams = { ...existingParams, ...newParams }
  }

  if (!Object.keys(newParams).length) {
    console.debug('[CampaignManager] No campaign parameters found to save')
    return false
  }

  // Filtra solo i parametri di campagna validi
  const filteredParams = _filterCampaignKeys(newParams)

  // Rifiuta i valori ETS che non rispettano il pattern consentito
  const safeParams = _filterParamValues(filteredParams)

  if (!Object.keys(safeParams).length) {
    console.info('[CampaignManager] No valid campaign parameters to save')
    return false
  }

  // Salva nel cookie
  const cookieOptions = {
    expires,
    path,
    secure: true,
    sameSite: 'Lax',
    ...(domain && { domain }),
  }

  try {
    Cookie.set(
      cookieName,
      encodeURIComponent(JSON.stringify(safeParams)),
      cookieOptions
    )

    // Log only the saved ETS keys, never their values (cookie tracking data).
    console.info(
      '[CampaignManager] Campaign parameters saved:',
      Object.keys(safeParams)
    )

    return true
  } catch (error) {
    console.error('[CampaignManager] Error saving campaign parameters:', error)
    return false
  }
}

/**
 * Rimuove parametri di campagna dal cookie
 *
 * @param {string} cookieName - Nome del cookie
 * @param {string | string[]} [keysToRemove] - Chiavi da rimuovere (se non specificato, rimuove tutti)
 * @returns {boolean} true se l'operazione è riuscita
 *
 * @example
 * removeCampaignParameters('campaign_utm', ['ets_cmmk', 'ets_sgmt'])
 * removeCampaignParameters('campaign_utm') // Rimuove il cookie completamente
 */
export function removeCampaignParameters(cookieName, keysToRemove = null) {
  if (!cookieName) {
    console.warn('[CampaignManager] cookieName is required')
    return false
  }

  try {
    if (!keysToRemove) {
      Cookie.remove(cookieName)
      console.info('[CampaignManager] Campaign cookie removed:', cookieName)
      return true
    }

    const keysArray = Array.isArray(keysToRemove)
      ? keysToRemove
      : [keysToRemove]
    const existingParams = getCampaignParameters(cookieName)
    const updatedParams = { ...existingParams }

    keysArray.forEach((key) => {
      delete updatedParams[key]
    })

    if (Object.keys(updatedParams).length) {
      setCampaignParameters({
        cookieName,
        customParams: updatedParams,
        override: true,
      })
    } else {
      Cookie.remove(cookieName)
    }

    console.info('[CampaignManager] Campaign parameters removed:', keysArray)
    return true
  } catch (error) {
    console.error(
      '[CampaignManager] Error removing campaign parameters:',
      error
    )
    return false
  }
}

/**
 * Costruisce una QueryStringManager con i parametri di campagna
 * Utile per concatenare parametri a URL o query string
 *
 * @param {string} cookieName - Nome del cookie
 * @param {string} [baseQueryString] - Query string base a cui aggiungere i parametri
 * @returns {QueryStringManager} QueryStringManager con i parametri di campagna
 *
 * @example
 * const qs = buildCampaignQueryString('campaign_utm')
 * const url = `https://example.com${qs.toSearchString()}`
 */
export function buildCampaignQueryString(cookieName, baseQueryString = '') {
  const campaignParams = getCampaignParameters(cookieName)
  const qs = new QueryStringManager(baseQueryString)
  return qs.merge(campaignParams)
}

/**
 * Hook React per Next.js - Gestisce i parametri di campagna lato client
 * Usa il nuovo App Router di Next.js 13+
 *
 * @param {string} cookieName - Nome del cookie
 * @param {Object} [options] - Opzioni aggiuntive
 * @param {boolean} [options.watchQueryChanges=true] - Se true, aggiorna i parametri quando la query cambia
 * @returns {QueryObject} Parametri di campagna attuali
 *
 * @example
 * export default function MyComponent() {
 *   const campaignParams = useCampaignParameters('campaign_utm')
 *   return <div>{JSON.stringify(campaignParams)}</div>
 * }
 */
export function useCampaignParameters(cookieName, options = {}) {
  const { watchQueryChanges = true } = options

  if (!cookieName) {
    console.warn(
      '[CampaignManager] cookieName is required for useCampaignParameters'
    )
    return {}
  }

  try {
    if (typeof window === 'undefined') {
      return {}
    }

    // Legge dalla query string attuale
    const queryString =
      typeof window !== 'undefined' ? window.location.search : ''
    return getCampaignParameters(cookieName, {}, queryString)
  } catch (error) {
    console.error('[CampaignManager] Error in useCampaignParameters:', error)
    return {}
  }
}

function _normalizeQueryString(queryString) {
  if (typeof queryString === 'string') return queryString
  if (queryString && typeof queryString === 'object') {
    return buildSearchParamsString(queryString)
  }
  return ''
}

function _pickCampaignParams(params) {
  return _filterParamValues(_filterCampaignKeys(params))
}

/**
 * Filtra i parametri mantenendo solo quelli di campagna validi
 *
 * @private
 * @param {QueryObject} params - Parametri da filtrare
 * @returns {QueryObject} Parametri filtrati
 */
function _filterCampaignKeys(params) {
  if (!params || typeof params !== 'object') return {}

  const filtered = {}
  CAMPAIGN_KEYS.forEach((key) => {
    if (key in params) {
      filtered[key] = params[key]
    }
  })
  return filtered
}

/**
 * Mantiene solo i parametri il cui valore rispetta ETS_PATTERN
 *
 * @private
 * @param {QueryObject} params - Parametri da validare
 * @returns {QueryObject} Parametri con valori validi
 */
function _filterParamValues(params) {
  const filtered = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    if (typeof value === 'string' && ETS_PATTERN.test(value)) {
      filtered[key] = value
    } else {
      console.warn(`[CampaignManager] Rejected invalid ETS value for "${key}"`)
    }
  })
  return filtered
}

/**
 * Ottiene la lista delle chiavi di campagna riconosciute
 *
 * @returns {string[]} Array con i nomi delle chiavi di campagna
 */
export function getCampaignKeys() {
  return [...CAMPAIGN_KEYS]
}

/**
 * Verifica se una chiave è una chiave di campagna valida
 *
 * @param {string} key - Chiave da verificare
 * @returns {boolean}
 */
export function isCampaignKey(key) {
  return CAMPAIGN_KEYS.includes(key)
}
